'use client'
import Logo from '@/assets/images/logo.png'
import { locales, type Locale } from '@/i18n/config'
import { Link, usePathname } from '@/i18n/navigation'
import { cn } from '@/lib/utils'
import { isAvailable } from '@/seo/routes'
import { LucideLogIn, LucideMenu, LucideX } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import Image from 'next/image'
import { type FC, useEffect, useId, useState } from 'react'

const APP_URL = 'https://app.opsynk.com/'

const NAV_LINKS = [
  { href: '/workorder', key: 'workorder' },
  { href: '/checklist', key: 'checklist' },
  { href: '/planning', key: 'planning' },
  { href: '/industries', key: 'industries' },
  { href: '/features', key: 'features' },
  { href: '/guides', key: 'guides' },
  { href: '/templates', key: 'templates' },
  { href: '/blog', key: 'blog' },
] as const

const LanguageSwitch: FC<{ pathname: string; locale: Locale; label: string }> = ({ pathname, locale, label }) => {
  const targetFor = (lang: Locale) => {
    if (pathname.startsWith('/blog/')) return '/blog'
    return isAvailable(pathname, lang) ? pathname : '/'
  }
  return (
    <div role="group" aria-label={label} className="flex items-center rounded-md border border-neutral-300 p-0.5 text-xs font-semibold">
      {locales.map((lang) => (
        <Link
          key={lang}
          href={targetFor(lang) as '/'}
          locale={lang}
          hrefLang={lang}
          lang={lang}
          aria-current={lang === locale ? 'true' : undefined}
          className={cn(
            'rounded-sm px-2 py-1 uppercase transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            lang === locale ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:text-neutral-900',
          )}
        >
          {lang === 'sv' ? 'SV' : 'EN'}
        </Link>
      ))}
    </div>
  )
}

const Navbar: FC = () => {
  const pathname = usePathname()
  const locale = useLocale() as Locale
  const t = useTranslations('Navbar')
  const [open, setOpen] = useState(false)
  const menuId = useId()

  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const isActive = (href: string) => (href === '/' ? pathname === '/' : pathname.startsWith(href))
  const links = NAV_LINKS.filter((link) => isAvailable(link.href, locale))

  return (
    <>
      <a
        href="#main-content"
        className="sr-only z-[60] rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        {t('skip_to_content')}
      </a>
      <header className="sticky top-0 z-50 w-full border-b border-neutral-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75">
        <div className="container flex h-16 items-center gap-6 px-4 md:px-6">
          <Link href="/" className="flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <Image src={Logo} alt="" width={32} height={32} className="h-8 w-8 rounded-md bg-primary p-1.5" />
            <span className="text-lg font-bold tracking-tight text-neutral-900">{t('title')}</span>
          </Link>

          <nav aria-label={t('navigation_label')} className="ml-auto hidden xl:block">
            <ul className="flex items-center gap-1">
              {links.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    aria-current={isActive(link.href) ? 'page' : undefined}
                    className={cn(
                      'rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                      isActive(link.href) ? 'text-primary' : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900',
                    )}
                  >
                    {t(`links.${link.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="ml-auto flex items-center gap-3 xl:ml-0">
            <a
              href={APP_URL}
              className="hidden items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:flex"
            >
              <LucideLogIn aria-hidden size={16} />
              {t('links.manager')}
            </a>
            <LanguageSwitch pathname={pathname} locale={locale} label={t('language_selector_label')} />
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-neutral-300 text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary xl:hidden"
              aria-expanded={open}
              aria-controls={menuId}
              aria-label={open ? t('close_menu') : t('open_menu')}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? <LucideX aria-hidden size={20} /> : <LucideMenu aria-hidden size={20} />}
            </button>
          </div>
        </div>

        <nav id={menuId} aria-label={t('navigation_label')} hidden={!open} className="border-t border-neutral-200 bg-white xl:hidden">
          <ul className="container flex flex-col px-4 py-2">
            {links.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  aria-current={isActive(link.href) ? 'page' : undefined}
                  className={cn(
                    'block border-b border-neutral-100 py-3 text-base font-medium',
                    isActive(link.href) ? 'text-primary' : 'text-neutral-800',
                  )}
                >
                  {t(`links.${link.key}`)}
                </Link>
              </li>
            ))}
            <li>
              <a href={APP_URL} className="flex items-center gap-2 py-3 text-base font-medium text-neutral-800">
                <LucideLogIn aria-hidden size={18} />
                {t('links.manager')}
              </a>
            </li>
          </ul>
        </nav>
      </header>
    </>
  )
}

export default Navbar
